import { Container, Graphics } from "pixi.js";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity, GameEventMap } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import {
  BenchGroup,
  BenchGroupMember,
  GroupPiece,
  groupPieces,
  memberFor,
  placementOnMember,
  seatInGroup,
} from "../../../game/bench-work/bench-group";
import { BenchPlacement } from "../../../game/bench-work/bench-layout";
import {
  atFlipStop,
  flipStopOf,
  nextFlipStop,
  tumbles,
} from "../../../game/bench-work/flip-cycle";
import { placedPieceSize } from "../../../game/bench-work/workpiece";
import { Machine } from "../../../game/Machine";
import { MaterialInstance, ToolItem } from "../../../game/Materials";
import { ToolId } from "../../../game/Tool";
import { toolMountRefusal } from "../../../game/tool-rack";
import {
  arrangeBenchMaterial,
  gatherBenchPieces,
  layCarriedOnBench,
  layStoredOnBench,
  stowBenchMaterial,
} from "../../../sim/commands/bench-commands";
import {
  findMachineEntity,
  stowMaterialsInMachine,
  takeInputsFromMachine,
  takeOutputsFromMachine,
  takeStoredMaterialsFromMachine,
} from "../../../sim/commands/machine-commands";
import {
  mountStoredTool,
  mountTool,
  unmountTool,
} from "../../../sim/commands/tool-commands";
import { MachineEntity } from "../../../sim/entities/MachineEntity";
import { Player } from "../../../sim/entities/Player";
import { ShellStore } from "../../ShellStore";
import { BenchAssemblyView } from "./BenchAssemblyView";
import { BenchDive, ToolSource } from "./BenchDive";
import { BenchDiveView } from "./BenchDiveView";
import { benchStage, pieceUnder, stagePointer } from "./benchStage";
import {
  BenchTrayView,
  tileFootprint,
  tilePad,
  trayUnderPointer,
} from "./BenchTrayView";
import { PieceMotion } from "./PieceMotion";
import { StageFit } from "./stageMath";
import { tileAngleDeg, tileScale, TrayKind } from "./trayMath";

/**
 * The bare hands at the bench: one rule for moving anything — press a
 * thing, carry it, let it go over a place it can rest. The places are
 * the run's tops, the two trays along the bottom of the view
 * (`BenchTrayView`: the shelf under the bench and the arms), and, for a
 * tool, the hooks on the rail (`BenchToolRail`). A piece is picked up
 * off any of them and dropped onto any that takes it, and it stays one
 * sprite on one holder the whole way, shrinking to tile size as it
 * crosses into a tray and growing back over the top. Turning it (R) and
 * tumbling a board onto its edge or end (F) ride along mid-carry.
 *
 * Where pieces lie is real state, not decoration — the shop floor draws
 * the same arrangement — so every drop commits through a command:
 * `arrangeBenchMaterial` on the top (a piece dragged across a seam is
 * bookkept by the table it came to rest on), `stowBenchMaterial` and
 * `takeInputsFromMachine` off the top into a tray, the lay commands out
 * of a tray onto the top, the stow/take pair between the trays, and
 * `mountTool` / `unmountTool` between the arms and the rail.
 *
 * A carried piece goes wherever the hand goes — off the edge of the
 * wood, across the backdrop, over the trays — nothing holds it back
 * mid-carry. Where it *rests* is another matter: stock overhangs a real
 * bench constantly, but a bench can't hold something balanced past its
 * own edge, so a piece let go with its middle off the wood springs back
 * to the nearest spot on the top (`seatInGroup`), and while it hangs
 * out there its landing is drawn as an outline on the edge so the
 * spring lands nowhere surprising. Let go over a tray that won't take
 * it (full), a piece springs back to where it was picked up, since the
 * sim never changed.
 *
 * A tool has one more place to rest: the hand itself. The hand is the
 * bench's mode (docs/bench-work.md decision 0), and a tool gets there
 * the way it gets anywhere — a press on it, wherever it lies, is the
 * pickup, and letting go over nothing that takes it (the top, the
 * open, a full tray, a hook it doesn't fit) leaves it in hand instead
 * of springing back. A press that never travels is the click: click a
 * tool on the rail, in the arms tray, or on the shelf tray and it is in
 * hand. The sim is untouched by the pickup (`BenchDive.held` remembers
 * where it came from); it moves when the tool is put down somewhere
 * else, and putting down is a click too — with a tool in hand, a press
 * on a tray sets it there (`putHeldTool`) and a press on a hook hangs
 * it (`hangHeldTool`, run by the rail's DOM), through the very drops a
 * carry commits. Escape and the right button put it back where it came
 * from. Using it hangs it: the work views settle the tool onto the rail
 * as the first stroke lands (`BenchDive.settleHeldTool`).
 *
 * A carried *tool* is the one thing not drawn on the canvas: the rail
 * it goes to is DOM chrome over the canvas, and a sprite under the
 * chrome would vanish behind the rail on its way to a hook. So while a
 * tool is carried the stage marker draws its icon riding the pointer
 * (`carriedTool`, the same seam the hand cursor uses — in the open it
 * sits exactly where the hand cursor will, so letting go into the hand
 * is seamless), sized to match whatever it is over, and its last leg to
 * a hook — or back to the one it came off — is a chrome flight
 * (`toolFlight`). A press that starts on a hook never reaches the
 * engine's mouse either, so the rail runs that press itself and hands
 * the tool in here (`beginHookDrag` / `endHookDrag`), and the hooks are
 * measured off the page as drop targets (`BENCH_HOOK_ATTRIBUTE`). A
 * tool let go into a tray announces itself there (`expectArrival`) so
 * its tile starts under the hand.
 *
 * With a tool in hand the pieces can't be moved — the tool's own
 * gesture owns the pointer over the top — but the trays and the hooks
 * still answer, lit as the places the tool can be put down. With a
 * clamp or the bottle in hand this view stands down entirely.
 */

/** Where a carried piece was picked up. */
export type DragSource =
  | { readonly kind: "top"; readonly member: BenchGroupMember }
  | { readonly kind: "shelf"; readonly member: BenchGroupMember }
  | { readonly kind: "arms" }
  | {
      readonly kind: "hook";
      readonly toolId: ToolId;
      readonly index: number;
      /** The hook's middle on screen, to fly back to. */
      readonly x: number;
      readonly y: number;
    };

/** Where a carried piece would go if let go right now — "hand" being
 * a tool's: it stays in hand wherever nothing else takes it. */
export type DropTarget = "top" | TrayKind | "hook" | "hand";

/** A hook on the rail, measured off the page. */
interface Hook {
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

interface Drag {
  readonly material: MaterialInstance;
  readonly from: DragSource;
  /** Live placement in the run's frame, committed on release. */
  placement: BenchPlacement;
  /** Where the hand took hold of it, so it doesn't snap to the cursor. */
  readonly grabDxIn: number;
  readonly grabDyIn: number;
  /** Begun on the rail's DOM: the engine never saw the press, so the
   * rail's own release ends it rather than the mouse state. */
  readonly fromDom: boolean;
  /** Where the press landed on screen, and whether it has moved far
   * enough since to be a drag rather than a click. */
  readonly pressX: number;
  readonly pressY: number;
  traveled: boolean;
}

/** The tool icon riding the pointer, for the chrome to draw. */
export interface CarriedTool {
  readonly toolId: ToolId;
  /** The icon's top-left corner on screen. */
  readonly x: number;
  readonly y: number;
  /** The icon's size on screen right now: the hook's over a hook, a
   * tile's over a tray, the hand cursor's in the open. */
  readonly size: number;
}

/** What the hand has up — carried by a press, or held as the mode. */
export interface Carrying {
  readonly toolId: ToolId;
  readonly fromKind: DragSource["kind"];
}

/** A tool's last leg to a hook on the rail, drawn by the chrome. */
export interface ToolFlight {
  /** Distinct per flight, so the chrome plays each from its start. */
  readonly id: number;
  readonly toolId: ToolId;
  readonly fromX: number;
  readonly fromY: number;
  readonly toX: number;
  readonly toY: number;
  readonly size: number;
  secondsLeft: number;
}

/** The icon's size on a hook, which a tool over one matches. */
const HOOK_ICON_PX = 64;
/** The hand cursor's icon (BenchStageMarker's HandCursor): its size and
 * where the pointer sits in it. A tool carried in the open matches it,
 * so letting go into the hand doesn't move the picture. */
export const HAND_ICON_PX = 48;
export const HAND_HOTSPOT = { x: 12, y: 11 } as const;
/** How long a tool's last leg to (or back to) a hook plays. */
export const TOOL_FLIGHT_SECONDS = 0.26;
/** The attribute the rail's free hooks wear, for measuring them as
 * drop targets; its value is the hook's index on the rail. */
export const BENCH_HOOK_ATTRIBUTE = "data-bench-hook";
/** How far a press travels before it is a drag rather than a click —
 * the rail's own threshold, so a press reads the same everywhere. */
const DRAG_THRESHOLD_PX = 6;
/** The landing outline's ink — the blueprint outlines' own. */
const LANDING_GHOST = 0xf5efe3;

export class BenchArrangeView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private root: Container & GameSprite;
  /** Where a piece hanging off the edge would come to rest, in outline. */
  private landing = new Graphics();
  private carried = new Container();
  private drag: Drag | null = null;
  private hoveredId: string | null = null;
  /** Where the carried piece would land — read by the trays and the
   * rail for their lit state, and by the chrome. Null while nothing is
   * carried, or while the hand is over nothing that takes the piece. */
  private dropTarget: DropTarget | null = null;
  /** The hook a carried tool is over, when `dropTarget` is "hook". */
  private dropHook: Hook | null = null;
  /** The carried piece's holder — R and F mid-drag spring and tumble on
   * it, the same motion the set-down pieces get. Never a tool's. */
  private carriedMotion: PieceMotion | null = null;
  /** The holder of the last-released piece, waiting for whichever view
   * draws it next to adopt it — the springs carry the drop into its
   * seat or its tray slot, or back where it came from. */
  private handoff: { id: string; motion: PieceMotion } | null = null;
  private flight: ToolFlight | null = null;
  private flightCount = 0;
  /** A press begun over a tray with a tool in hand: released over the
   * same tray, it sets the tool down there. */
  private putPress: TrayKind | null = null;

  constructor() {
    super();
    // Placed in the dive's own frame ("parent" — this view is the dive
    // view's child), so the lean-in carries every surface on the stage
    // as one picture.
    this.root = new Container() as Container & GameSprite;
    this.root.layerName = "parent";
    this.root.addChild(this.landing, this.carried);
    this.sprite = this.root;
  }

  /** Where the carried piece — or the tool in hand, were it put down —
   * would go right now, for the chrome and the trays. */
  dropTargetKind(): DropTarget | null {
    return this.drag || this.toolInHand() ? this.dropTarget : null;
  }

  /** Which hook a carried or held tool would hang on, when it's over one. */
  dropHookIndex(): number | null {
    return this.dropTargetKind() === "hook"
      ? (this.dropHook?.index ?? null)
      : null;
  }

  /** Where the carried piece came from, or null with nothing carried. */
  dragSource(): DragSource | null {
    return this.drag?.from ?? null;
  }

  /** What the hand is carrying, or null. */
  dragMaterial(): MaterialInstance | null {
    return this.drag?.material ?? null;
  }

  /**
   * The tool the hand has up, carried by a press or held as the mode —
   * what the trays and the hooks light themselves for. Null while the
   * hand carries stock or nothing.
   */
  carrying(): Carrying | null {
    const drag = this.drag;
    if (drag) {
      return drag.material.type === "tool"
        ? { toolId: drag.material.toolId, fromKind: drag.from.kind }
        : null;
    }
    const held = this.toolInHand();
    return held ? { toolId: held.toolId, fromKind: held.from.kind } : null;
  }

  /** The tool riding the pointer, for the chrome to draw (null while
   * the hand carries stock or nothing; a *held* tool is the hand cursor's). */
  carriedTool(): CarriedTool | null {
    const drag = this.drag;
    if (!drag || drag.material.type !== "tool") return null;
    const [px, py] = this.game.io.mousePosition;
    const fit = benchStage(this.game)?.fit;
    const overTray = this.dropTarget === "shelf" || this.dropTarget === "arms";
    if (this.dropTarget === "hook" || (overTray && fit)) {
      const size =
        this.dropTarget === "hook" ? HOOK_ICON_PX : tilePx(drag.material, fit!);
      return {
        toolId: drag.material.toolId,
        x: px - size / 2,
        y: py - size / 2,
        size,
      };
    }
    return {
      toolId: drag.material.toolId,
      x: px - HAND_HOTSPOT.x,
      y: py - HAND_HOTSPOT.y,
      size: HAND_ICON_PX,
    };
  }

  /** The tool on its last leg to a hook, for the chrome to draw. */
  toolFlight(): ToolFlight | null {
    return this.flight;
  }

  /** The piece the hand is over (or carrying), for the stage marker. */
  handPieceId(): string | null {
    return this.drag?.material.id ?? this.hoveredId;
  }

  draggingId(): string | null {
    return this.drag?.material.id ?? null;
  }

  /** The view that draws the released piece next collects its holder
   * here, in-flight springs and all. */
  takeHandoff(materialId: string): PieceMotion | null {
    if (this.handoff?.id !== materialId) return null;
    const motion = this.handoff.motion;
    this.handoff = null;
    return motion;
  }

  /** Where the hand has the dragged piece right now. The sim keeps the
   * placement it was picked up from until release, so anything that
   * has to follow the piece mid-drag reads this instead. */
  dragPlacement(): BenchPlacement | null {
    return this.drag?.placement ?? null;
  }

  /**
   * Whose shelf what the hand has up would go onto if let go over the
   * shelf tray — the table it came off, else the opened one, else any
   * in the run with room (`shelfMemberFor`) — so the tray can light the
   * slot among that table's tiles. Null when every shelf is full.
   */
  shelfLanding(): BenchGroupMember | null {
    const stage = benchStage(this.game);
    if (!stage) return null;
    const from = this.drag?.from;
    const nearest =
      from?.kind === "top"
        ? from.member
        : openedMember(stage.opened, stage.group);
    return shelfMemberFor(stage.group, nearest);
  }

  /**
   * Whether the rail's hooks would take what the hand has up: only a
   * tool, and only one the opened station hangs (`toolMountRefusal`). A
   * tool that came off a hook can always go back on one.
   */
  hookAccepts(): boolean {
    const carrying = this.carrying();
    return carrying !== null && this.hookTakes(carrying);
  }

  private hookTakes(carrying: Carrying): boolean {
    if (carrying.fromKind === "hook") return true;
    const opened = benchStage(this.game)?.opened;
    return (
      opened !== undefined && toolMountRefusal(opened, carrying.toolId) === null
    );
  }

  /**
   * The rail's click on a hook with a tool in hand: hang it there. A
   * tool that came off the rail is already hanging — the hand just
   * lets go of it, and the picture flies it to its hook; one out of a
   * tray mounts. The digit keys reach here too.
   */
  hangHeldTool(index: number): boolean {
    const held = this.toolInHand();
    if (!held) return false;
    const hook = this.hookAt(index);
    return this.putHeldTool("hook", hook);
  }

  /**
   * The rail's press on a mounted tool, once it has moved far enough to
   * be a drag: the tool comes off its hook into the hand, and the hook
   * shows empty behind it. The sim is untouched until the drop.
   */
  beginHookDrag(toolId: ToolId, index: number, x: number, y: number): void {
    if (!this.handsFree() || this.drag) return;
    const stage = this.stage();
    if (!stage) return;
    const { fit, pointer } = stage;
    const material: ToolItem = {
      id: `hook:${index}:${toolId}`,
      type: "tool",
      toolId,
    };
    const xIn = (x - fit.originX) / fit.pxPerIn;
    const yIn = (y - fit.originY) / fit.pxPerIn;
    this.drag = {
      material,
      from: { kind: "hook", toolId, index, x, y },
      placement: { xIn, yIn, angleDeg: 0, flipped: false },
      grabDxIn: xIn - pointer.xIn,
      grabDyIn: yIn - pointer.yIn,
      fromDom: true,
      pressX: x,
      pressY: y,
      traveled: true,
    };
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  /** The rail's release, ending the drag it began. */
  endHookDrag(): void {
    if (this.drag?.fromDom) this.commitDrag();
  }

  private dive(): BenchDive | undefined {
    return this.game.entities.tryGetSingleton(BenchDive);
  }

  private trays(): BenchTrayView | undefined {
    return this.game.entities.tryGetSingleton(BenchTrayView);
  }

  /** Whether the hands are free to arrange — no tool, no clamp, no
   * bottle: whatever the hands are carrying owns the pointer. Only once
   * the lean-in has landed, so a piece can't be grabbed by where it is
   * about to be. */
  private handsFree(): boolean {
    const dive = this.dive();
    return dive != null && dive.workable() && !dive.handsFull();
  }

  /** The tool in hand as the mode, once the lean-in has landed. */
  private toolInHand(): { toolId: ToolId; from: ToolSource } | null {
    const dive = this.dive();
    if (!dive?.workable() || dive.held?.kind !== "tool") return null;
    return { toolId: dive.held.toolId, from: dive.held.from };
  }

  private stage(): {
    group: BenchGroup;
    fit: StageFit;
    pointer: { xIn: number; yIn: number };
  } | null {
    const stage = benchStage(this.game);
    if (!stage) return null;
    return {
      group: stage.group,
      fit: stage.fit,
      pointer: stagePointer(this.game, stage.fit),
    };
  }

  /** The piece under the hand on the top: the one being dragged (if it
   * came off the top), or hovered. */
  private handPiece(group: BenchGroup): GroupPiece | null {
    const id = this.drag?.material.id ?? this.hoveredId;
    if (!id) return null;
    return groupPieces(group).find((piece) => piece.material.id === id) ?? null;
  }

  /** Parts a driven fastener holds — nailed on, so they don't move. */
  private fastened(): ReadonlySet<string> {
    return (
      this.game.entities.tryGetSingleton(BenchAssemblyView)?.fastened() ??
      new Set()
    );
  }

  /** The machine entity behind a member of the run. */
  private entityFor(member: BenchGroupMember): MachineEntity | null {
    return findMachineEntity(this.game, member.machine.state);
  }

  /** The opened table's entity — whose rail the hooks belong to. */
  private openedEntity(): MachineEntity | null {
    return this.dive()?.openBench() ?? null;
  }

  /** The rail's free hooks, measured off the page — only asked for
   * while a tool is up, so the rail costs nothing otherwise. */
  private hookUnder(px: number, py: number): Hook | null {
    for (const node of document.querySelectorAll(`[${BENCH_HOOK_ATTRIBUTE}]`)) {
      const hook = measureHook(node);
      if (
        px >= hook.x &&
        py >= hook.y &&
        px <= hook.x + hook.width &&
        py <= hook.y + hook.height
      ) {
        return hook;
      }
    }
    return null;
  }

  /** A bare hook by its index on the rail, measured off the page. */
  private hookAt(index: number): Hook | null {
    const node = document.querySelector(`[${BENCH_HOOK_ATTRIBUTE}="${index}"]`);
    return node ? measureHook(node) : null;
  }

  /** Where a tool hangs on the rail, measured off its button — the spot
   * a tool taken off the rail flies back to. */
  private railSpotOf(toolId: ToolId): { x: number; y: number } | null {
    const node = document.querySelector(`[data-testid="bench-tool-${toolId}"]`);
    if (!node) return null;
    const rect = node.getBoundingClientRect();
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  @on("mouseDown")
  onMouseDown() {
    if (this.toolInHand()) {
      // A press over a tray with a tool in hand starts setting it down
      // there (the work views stand down over the trays).
      const [px, py] = this.game.io.mousePosition;
      this.putPress = this.stage() ? trayUnderPointer(this.game) : null;
      return;
    }
    if (!this.handsFree()) return;
    const stage = this.stage();
    if (!stage) return;
    const { group, fit, pointer } = stage;
    // The trays are chrome: over one, the hand takes the tile there and
    // nothing else, even where a piece hangs off the bench under it.
    const [px, py] = this.game.io.mousePosition;
    const overTray = trayUnderPointer(this.game) !== null;
    const piece = overTray ? null : pieceUnder(group, pointer.xIn, pointer.yIn);
    if (piece) {
      // A part a fastener already holds is part of the build: no
      // dragging it back off.
      if (this.fastened().has(piece.material.id)) return;
      // Picking a piece up trues it: the hand squares the small tilt it
      // landed with, so setting it against a laid-out plan doesn't start
      // with a nudging match. Squaring turns it about its middle — the
      // very point being held — so nothing moves.
      this.drag = {
        material: piece.material,
        from: { kind: "top", member: piece.member },
        placement: {
          ...piece.placement,
          angleDeg: nearestRightAngle(piece.placement.angleDeg),
        },
        grabDxIn: piece.placement.xIn - pointer.xIn,
        grabDyIn: piece.placement.yIn - pointer.yIn,
        fromDom: false,
        pressX: px,
        pressY: py,
        traveled: false,
      };
      this.game.entities.tryGetSingleton(ShellStore)?.bump();
      return;
    }
    // Nothing on the top under the hand: a tile on a tray, then. It
    // comes up at tile size, lying as it lay in the slot. (A tool's
    // tile becomes the chrome's icon — see the class note.)
    const tile = this.trays()?.tileUnder(px, py);
    if (!tile) return;
    const xIn = (tile.x - fit.originX) / fit.pxPerIn;
    const yIn = (tile.y - fit.originY) / fit.pxPerIn;
    this.drag = {
      material: tile.material,
      from: tile.place,
      placement: { xIn, yIn, angleDeg: tile.angleDeg, flipped: false },
      grabDxIn: xIn - pointer.xIn,
      grabDyIn: yIn - pointer.yIn,
      fromDom: false,
      pressX: px,
      pressY: py,
      traveled: false,
    };
    if (tile.material.type !== "tool") {
      this.carriedMotion = new PieceMotion(
        this.game.entities.getSingleton(BenchDiveView).xray,
      );
      this.carriedMotion.setTileScale(tile.scale, { snap: true });
      this.carriedMotion.setLit(true);
      this.carriedMotion.setCarried(true);
      this.carried.addChild(this.carriedMotion.holder);
    }
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("mouseUp")
  onMouseUp() {
    if (this.drag && !this.drag.fromDom) this.commitDrag();
    const put = this.putPress;
    this.putPress = null;
    if (put && this.toolInHand()) {
      const [px, py] = this.game.io.mousePosition;
      if (this.trays()?.trayUnder(px, py) === put) this.putHeldTool(put, null);
    }
  }

  @on("tick")
  onTick(dt: number) {
    if (this.flight) {
      this.flight.secondsLeft -= dt;
      if (this.flight.secondsLeft <= 0) {
        this.flight = null;
        this.game.entities.tryGetSingleton(ShellStore)?.bump();
      }
    }
    if (!this.handsFree()) {
      if (this.drag) this.commitDrag();
      this.hoveredId = null;
      // With a tool in hand the trays and the hooks are the places it
      // can be put down, and light the way they do under a carry.
      this.aimHeldTool();
      return;
    }
    this.putPress = null;
    const stage = this.stage();
    if (!stage) return;

    if (this.drag) {
      if (!this.drag.fromDom && !this.game.io.lmb) {
        this.commitDrag();
        return;
      }
      // The piece follows the hand wherever it goes; where it would
      // come to rest is settled on release (`dropOnTop` seats it).
      const held = {
        ...this.drag.placement,
        xIn: stage.pointer.xIn + this.drag.grabDxIn,
        yIn: stage.pointer.yIn + this.drag.grabDyIn,
      };
      const [px, py] = this.game.io.mousePosition;
      if (
        !this.drag.traveled &&
        Math.hypot(px - this.drag.pressX, py - this.drag.pressY) >=
          DRAG_THRESHOLD_PX
      ) {
        this.drag.traveled = true;
      }
      const trays = this.trays();
      const tray = trays?.trayUnder(px, py) ?? null;
      const hook =
        !tray && this.drag.material.type === "tool"
          ? this.hookUnder(px, py)
          : null;
      const taken: DropTarget | null = tray
        ? trays?.accepts(tray, this.drag.from.kind)
          ? tray
          : null
        : hook
          ? this.hookAccepts()
            ? "hook"
            : null
          : liesOnTop(this.drag.material)
            ? "top"
            : null;
      // A tool over nothing that takes it stays in hand; a piece has
      // nowhere to go but back.
      const target =
        taken ?? (this.drag.material.type === "tool" ? "hand" : null);
      this.drag.placement = held;
      this.setDropTarget(target, hook);
      return;
    }

    const [px, py] = this.game.io.mousePosition;
    const under =
      trayUnderPointer(this.game) !== null
        ? (this.trays()?.tileUnder(px, py)?.material ?? null)
        : (pieceUnder(stage.group, stage.pointer.xIn, stage.pointer.yIn)
            ?.material ?? null);
    const hovered = under?.id ?? null;
    if (hovered !== this.hoveredId) {
      this.hoveredId = hovered;
      // The chips read the piece under the hand (F names the stop it
      // would reach), so a new piece under the pointer is a redraw.
      this.game.entities.tryGetSingleton(ShellStore)?.bump();
    }
  }

  private setDropTarget(target: DropTarget | null, hook: Hook | null): void {
    if (target === this.dropTarget && hook?.index === this.dropHook?.index) {
      return;
    }
    this.dropTarget = target;
    this.dropHook = hook;
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  /**
   * Where the tool in hand would be put down right now: the tray or the
   * hook under the pointer, when it takes the tool. Read by the trays
   * and the rail for their lit state, like a carry's target.
   */
  private aimHeldTool(): void {
    const carrying = this.carrying();
    if (!carrying || this.drag) {
      this.setDropTarget(null, null);
      return;
    }
    const [px, py] = this.game.io.mousePosition;
    const trays = this.trays();
    const tray = trays?.trayUnder(px, py) ?? null;
    const hook = tray ? null : this.hookUnder(px, py);
    const target: DropTarget | null = tray
      ? trays?.accepts(tray, carrying.fromKind)
        ? tray
        : null
      : hook && this.hookTakes(carrying)
        ? "hook"
        : null;
    this.setDropTarget(target, hook);
  }

  /**
   * Set the tool in hand down: into a tray, or on a hook. The hand
   * empties once the sim has taken it — a tool that came from that very
   * tray just goes back (nothing to commit), and one off the rail put on
   * a hook is already hanging, so it flies home and the hand lets go.
   */
  private putHeldTool(target: TrayKind | "hook", hook: Hook | null): boolean {
    const dive = this.dive();
    const held = this.toolInHand();
    const stage = this.stage();
    if (!dive || !held || !stage) return false;
    const { toolId, from } = held;
    let done: boolean;
    switch (target) {
      case "arms":
        done = this.putToolInArms(toolId, from);
        break;
      case "shelf":
        done = this.putToolOnShelf(stage.group, toolId, from);
        break;
      case "hook": {
        done = this.putToolOnHook(toolId, from, hook);
        if (done) {
          const to =
            from.kind === "hook" ? this.railSpotOf(toolId) : hookSpot(hook);
          if (to) this.flyToolTo(toolId, to.x, to.y);
        }
        break;
      }
    }
    if (!done) return false;
    dive.hold(null);
    this.setDropTarget(null, null);
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
    return true;
  }

  /** Let the piece go: onto the top where the hand left it, into the
   * tray or onto the hook it's over, into the hand (a tool over nothing
   * else), or — a piece over nothing that takes it — back where it came
   * from. A press that never traveled is a click, and a click on a
   * tool takes it in hand wherever it lay — even over the very tray it
   * came from, where a drag would be putting it back. */
  private commitDrag(): void {
    const drag = this.drag;
    this.drag = null;
    const click = drag !== null && !drag.traveled;
    const target: DropTarget | null = click
      ? drag.material.type === "tool"
        ? "hand"
        : null
      : this.dropTarget;
    const hook = click ? null : this.dropHook;
    this.dropTarget = null;
    this.dropHook = null;
    // The carried holder rides on: whichever view draws the piece next
    // adopts it, so the drop springs into its seat, its slot, or back
    // to its old place instead of reappearing there.
    const motion = this.carriedMotion;
    this.carriedMotion = null;
    if (motion) {
      this.carried.removeChild(motion.holder);
      this.handoff?.motion.destroy();
      this.handoff = drag ? { id: drag.material.id, motion } : null;
      if (!this.handoff) motion.destroy();
    }
    const stage = this.stage();
    if (!drag || !stage) return;
    const hung = this.openedEntity()?.state.tools.length ?? 0;
    switch (target) {
      case "top":
        this.dropOnTop(stage.group, drag);
        break;
      case "shelf":
        this.dropOnShelf(stage.group, drag);
        break;
      case "arms":
        this.dropInArms(drag);
        break;
      case "hook":
        if (hook) this.dropOnHook(drag, hook);
        break;
      case "hand":
        this.takeInHand(drag);
        break;
      default:
        break;
    }
    // A tool that came off a hook and is still on the rail flies back
    // to its hook, whether nothing took it or it was let go over its own
    // (or another) hook — the rack keeps no order. Into the hand it
    // stays up: the hook shows it faded, the cursor carries it.
    if (
      drag.from.kind === "hook" &&
      target !== "hand" &&
      (this.openedEntity()?.state.tools.length ?? 0) === hung
    ) {
      const to = hook && target === "hook" ? hookCenter(hook) : drag.from;
      this.flyToolTo(drag.from.toolId, to.x, to.y);
    }
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  /** A carried tool let go into the hand: it is the mode now, and it
   * remembers where it came from for Escape. The sim is untouched. */
  private takeInHand(drag: Drag): void {
    if (drag.material.type !== "tool") return;
    this.dive()?.hold({
      kind: "tool",
      toolId: drag.material.toolId,
      from: toolSourceOf(drag),
    });
  }

  /** The chrome's last leg for a tool: from the pointer to a hook. */
  private flyToolTo(toolId: ToolId, toX: number, toY: number): void {
    const [fromX, fromY] = this.game.io.mousePosition;
    this.flight = {
      id: ++this.flightCount,
      toolId,
      fromX,
      fromY,
      toX,
      toY,
      size: HOOK_ICON_PX,
      secondsLeft: TOOL_FLIGHT_SECONDS,
    };
  }

  /** A tool let go into a tray has no holder to hand over (it was the
   * chrome's icon while carried): tell the tray to start its tile under
   * the hand. */
  private announceToolArrival(kind: TrayKind, id: string): void {
    const [x, y] = this.game.io.mousePosition;
    this.trays()?.expectArrival(kind, id, x, y);
  }

  private dropOnTop(group: BenchGroup, drag: Drag): void {
    if (drag.from.kind === "hook") return;
    // Released near a blueprint outline, the part settles onto it — a
    // slot is the one thing that may seat a piece off the bench, since
    // a plan bigger than the top reaches past the edges on purpose.
    const snapped =
      this.game.entities
        .tryGetSingleton(BenchAssemblyView)
        ?.snapFor(drag.material, drag.placement) ?? null;
    const landing = snapped
      ? {
          placement: snapped,
          member:
            drag.from.kind === "arms"
              ? group.members[0]
              : memberOf(group, drag.material.id, drag.from.member),
        }
      : seatInGroup(group, drag.placement);
    const onto = this.entityFor(landing.member);
    if (!onto) return;
    const placement = placementOnMember(
      group,
      landing.member,
      landing.placement,
    );
    switch (drag.from.kind) {
      case "top": {
        // Dragged across a seam: the table it came to rest on bookkeeps
        // it now, and its arrangement is measured in that table's inches.
        if (drag.from.member.key !== landing.member.key) {
          gatherBenchPieces(this.game, onto, [drag.material.id]);
        }
        arrangeBenchMaterial(this.game, onto, drag.material.id, placement);
        return;
      }
      case "arms":
        layCarriedOnBench(this.game, onto, drag.material.id, placement);
        return;
      case "shelf": {
        // Up off the shelf of the table holding it, then across the seam
        // if the hand carried it onto a neighbour.
        const holder = this.entityFor(drag.from.member);
        if (!holder) return;
        const own = placementOnMember(
          group,
          drag.from.member,
          landing.placement,
        );
        if (!layStoredOnBench(this.game, holder, drag.material.id, own)) {
          return;
        }
        if (drag.from.member.key !== landing.member.key) {
          gatherBenchPieces(this.game, onto, [drag.material.id]);
        }
        return;
      }
    }
  }

  private dropOnShelf(group: BenchGroup, drag: Drag): void {
    if (drag.from.kind === "shelf") return;
    // Onto the shelf of the table it came off (or the opened one, out of
    // the arms or off the rail), else any table in the run with room.
    const nearest =
      drag.from.kind === "top"
        ? drag.from.member
        : openedMember(benchStage(this.game)?.opened, group);
    if (drag.material.type === "tool") {
      this.putToolOnShelf(group, drag.material.toolId, toolSourceOf(drag));
      return;
    }
    const shelf = shelfMemberFor(group, nearest);
    if (!shelf) return;
    const onto = this.entityFor(shelf);
    if (!onto) return;
    if (drag.from.kind === "arms") {
      stowMaterialsInMachine(this.game, [drag.material], onto);
      return;
    }
    if (drag.from.kind === "hook") return;
    // A piece another table bookkeeps slides onto this one first, the
    // way a drag across a seam does, so the shelf and the piece share a
    // station.
    if (drag.from.member.key !== shelf.key) {
      gatherBenchPieces(this.game, onto, [drag.material.id]);
    }
    this.hoveredId = null;
    stowBenchMaterial(this.game, onto, drag.material.id);
  }

  private dropInArms(drag: Drag): void {
    if (drag.from.kind === "arms") return;
    if (drag.material.type === "tool") {
      this.putToolInArms(drag.material.toolId, toolSourceOf(drag));
      return;
    }
    if (drag.from.kind === "hook") return;
    const from = this.entityFor(drag.from.member);
    if (!from) return;
    this.hoveredId = null;
    if (drag.from.kind === "shelf") {
      const stored = from.state.storedMaterials?.find(
        (m) => m.id === drag.material.id,
      );
      if (!stored) return;
      takeStoredMaterialsFromMachine(this.game, [stored], from);
      return;
    }
    // Off the top: out of whichever bay it lies in, by the live
    // instance — a pry may have rewritten the piece under its id.
    const output = from.state.outputMaterials.find(
      (m) => m.id === drag.material.id,
    );
    if (output) {
      takeOutputsFromMachine(this.game, [output], from);
      return;
    }
    const input = from.state.inputMaterials.find(
      (m) => m.id === drag.material.id,
    );
    if (input) takeInputsFromMachine(this.game, [input], from);
  }

  /**
   * Hang a carried tool on the hook. One that came off a hook just goes
   * back on, which `commitDrag` plays as the flight home. Either way the
   * chrome carries the icon the last leg to the hook, where the rail's
   * own icon takes over.
   */
  private dropOnHook(drag: Drag, hook: Hook): void {
    const carried = drag.material;
    if (carried.type !== "tool" || drag.from.kind === "hook") return;
    if (this.putToolOnHook(carried.toolId, toolSourceOf(drag), hook)) {
      const to = hookCenter(hook);
      this.flyToolTo(carried.toolId, to.x, to.y);
    }
  }

  // -------------------------------------------------------------------
  // Putting a tool down — the one set of moves under a carry's release
  // and a held tool's click alike. Each says whether the sim took it.
  // -------------------------------------------------------------------

  /** Out of the arms (`mountTool`) or straight off the shelf
   * (`mountStoredTool` — it never passes through the arms, so full
   * hands don't matter). One off the rail is hanging already. */
  private putToolOnHook(
    toolId: ToolId,
    from: ToolSource,
    hook: Hook | null,
  ): boolean {
    if (from.kind === "hook") return true;
    if (!hook) return false;
    const bench = this.openedEntity();
    if (!bench) return false;
    if (from.kind === "shelf") {
      const holder = this.memberEntity(from.memberKey);
      const stored = holder?.state.storedMaterials?.find(
        (m): m is ToolItem => m.id === from.itemId && m.type === "tool",
      );
      if (!holder || !stored) return false;
      return mountStoredTool(this.game, holder, bench, stored);
    }
    const tool = this.carriedItem(from.itemId);
    return tool !== null && mountTool(this.game, bench, tool);
  }

  /** Off the rail (`unmountTool`) or up off the shelf; one out of the
   * arms is there already. */
  private putToolInArms(toolId: ToolId, from: ToolSource): boolean {
    switch (from.kind) {
      case "arms":
        return true;
      case "hook": {
        const tool = this.unmount(toolId);
        if (tool) this.announceToolArrival("arms", tool.id);
        return tool !== null;
      }
      case "shelf": {
        const holder = this.memberEntity(from.memberKey);
        const stored = holder?.state.storedMaterials?.find(
          (m) => m.id === from.itemId,
        );
        if (!holder || !stored) return false;
        this.announceToolArrival("arms", stored.id);
        takeStoredMaterialsFromMachine(this.game, [stored], holder);
        return true;
      }
    }
  }

  /** Onto the shelf of the opened table, else any table in the run with
   * room: out of the arms, or off the rail through the arms and straight
   * on. One off a shelf is there already. */
  private putToolOnShelf(
    group: BenchGroup,
    toolId: ToolId,
    from: ToolSource,
  ): boolean {
    if (from.kind === "shelf") return true;
    const shelf = shelfMemberFor(
      group,
      openedMember(benchStage(this.game)?.opened, group),
    );
    const onto = shelf ? this.entityFor(shelf) : null;
    if (!onto) return false;
    const tool =
      from.kind === "arms"
        ? this.carriedItem(from.itemId)
        : this.unmount(toolId);
    if (!tool) return false;
    this.announceToolArrival("shelf", tool.id);
    stowMaterialsInMachine(this.game, [tool], onto);
    return true;
  }

  /** A tool riding in the arms, by its item id. */
  private carriedItem(itemId: string): ToolItem | null {
    return (
      this.game.entities
        .tryGetSingleton(Player)
        ?.inventory.find(
          (m): m is ToolItem => m.id === itemId && m.type === "tool",
        ) ?? null
    );
  }

  /** The table in the run with this machineKey. */
  private memberEntity(memberKey: string): MachineEntity | null {
    const group = benchStage(this.game)?.group;
    const member = group?.members.find((m) => m.key === memberKey);
    return member ? this.entityFor(member) : null;
  }

  /** Take a tool off the rail into the arms, and say which item the sim
   * minted for it. */
  private unmount(toolId: ToolId): ToolItem | null {
    const bench = this.openedEntity();
    const player = this.game.entities.tryGetSingleton(Player);
    if (!bench || !player) return null;
    const before = new Set(player.inventory.map((m) => m.id));
    if (!unmountTool(this.game, bench, toolId)) return null;
    return (
      player.inventory.find(
        (m): m is ToolItem => m.type === "tool" && !before.has(m.id),
      ) ?? null
    );
  }

  /**
   * R turns the piece under the hand a quarter turn; F tumbles a board
   * through its three stops (flat, up on edge, up on end) and turns
   * anything else over. Mid-drag the turn rides along and the release
   * commits both; a carried tile turns too.
   */
  @on("keyDown")
  onKeyDown({ key, event }: GameEventMap["keyDown"]) {
    if (!this.handsFree()) return;
    if (key !== "KeyR" && key !== "KeyF") return;
    const stage = this.stage();
    if (!stage) return;
    const piece = this.handPiece(stage.group);
    const material = this.drag?.material ?? piece?.material;
    const current = this.drag?.placement ?? piece?.placement;
    if (!material || !current) return;
    if (this.fastened().has(material.id)) return;
    event.preventDefault();
    event.stopImmediatePropagation();

    // One flip verb: a board cycles flat → up on its long edge → up on
    // its end → flat again; anything else turns over. Mirroring a board
    // face-for-face never showed anyway. R trues as it turns — a piece
    // lying at a small tilt lands square, not tilted a quarter further.
    const turned: BenchPlacement =
      key === "KeyR"
        ? { ...current, angleDeg: nearestRightAngle(current.angleDeg) + 90 }
        : tumbles(material)
          ? atFlipStop(current, nextFlipStop(flipStopOf(current)))
          : { ...current, flipped: !current.flipped };

    if (this.drag) {
      this.drag.placement = turned;
      return;
    }
    if (!piece) return;
    const holder = this.entityFor(piece.member);
    if (!holder) return;
    arrangeBenchMaterial(
      this.game,
      holder,
      piece.material.id,
      placementOnMember(stage.group, piece.member, turned),
    );
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("render")
  onRender(dt: number) {
    const stage = benchStage(this.game);
    if (!stage || !this.handsFree() || this.drag?.material.type === "tool") {
      this.clearCarried();
      return;
    }
    const { fit } = stage;

    // The piece riding the hand, drawn where the hand has it — position
    // tracks the pointer directly, while R and F play on the holder's
    // own springs, the same motion the set-down pieces get. It stays lit
    // the way it was under the hover, so grabbing doesn't put it out.
    // (The idle hover light itself rides the piece entities.) Over a
    // tray it shrinks toward that tray's tile size and turns to lie
    // along the slot; over the top it is full size.
    if (this.drag) {
      if (!this.carriedMotion) {
        // Taken hold of where it lies: motion starts from rest, then
        // lifts — the piece grows a hair toward the eye and its shadow
        // spreads, the way a thing picked up comes off the surface.
        this.carriedMotion = new PieceMotion(
          this.game.entities.getSingleton(BenchDiveView).xray,
        );
        this.carriedMotion.setLit(true);
        this.carriedMotion.setCarried(true);
        this.carried.addChild(this.carriedMotion.holder);
      }
      const material = this.drag.material;
      const overTray =
        this.dropTarget === "shelf" || this.dropTarget === "arms";
      this.carriedMotion.setTileScale(
        overTray ? (this.trays()?.tileScaleFor(material, fit) ?? 1) : 1,
      );
      const shown = overTray
        ? {
            ...this.drag.placement,
            angleDeg: tileAngleDeg(tileFootprint(material)),
          }
        : this.drag.placement;
      this.carriedMotion.retarget(material, shown, fit, {
        snapPosition: true,
      });
      this.carriedMotion.step(dt);
      this.drawLanding(stage.group, this.drag, fit);
      return;
    }
    this.clearCarried();
  }

  /**
   * The outline where a piece hanging off the edge of the wood would
   * come to rest if let go now — the seat `dropOnTop` would give it.
   * Nothing is drawn while the piece lies over the top (it rests where
   * it is), over a tray or a hook (it's leaving the top), or near a
   * blueprint outline (the outline itself lights, and the part goes
   * where the outline says).
   */
  private drawLanding(group: BenchGroup, drag: Drag, fit: StageFit): void {
    const g = this.landing;
    g.clear();
    if (this.dropTarget !== "top") return;
    const snapped = this.game.entities
      .tryGetSingleton(BenchAssemblyView)
      ?.snapFor(drag.material, drag.placement);
    if (snapped) return;
    const seat = seatInGroup(group, drag.placement).placement;
    if (seat.xIn === drag.placement.xIn && seat.yIn === drag.placement.yIn) {
      return;
    }
    const size = placedPieceSize(drag.material, seat);
    const radians = (seat.angleDeg * Math.PI) / 180;
    const cos = Math.cos(radians);
    const sin = Math.sin(radians);
    const halfW = size.widthIn / 2;
    const halfH = size.heightIn / 2;
    const corners = [
      [-halfW, -halfH],
      [halfW, -halfH],
      [halfW, halfH],
      [-halfW, halfH],
    ].flatMap(([dx, dy]) => [
      fit.originX + (seat.xIn + dx * cos - dy * sin) * fit.pxPerIn,
      fit.originY + (seat.yIn + dx * sin + dy * cos) * fit.pxPerIn,
    ]);
    g.poly(corners)
      .fill({ color: LANDING_GHOST, alpha: 0.08 })
      .stroke({ width: 2, color: LANDING_GHOST, alpha: 0.55 });
  }

  private clearCarried(): void {
    this.landing.clear();
    if (!this.carriedMotion) return;
    this.carriedMotion.destroy();
    this.carriedMotion = null;
  }
}

/** A tool's size as a tray tile, in screen pixels. */
function tilePx(material: MaterialInstance, fit: StageFit): number {
  const size = tileFootprint(material);
  return (
    tileScale(size, fit.pxPerIn, tilePad(material)) * size.widthIn * fit.pxPerIn
  );
}

function hookCenter(hook: Hook): { x: number; y: number } {
  return { x: hook.x + hook.width / 2, y: hook.y + hook.height / 2 };
}

function hookSpot(hook: Hook | null): { x: number; y: number } | null {
  return hook ? hookCenter(hook) : null;
}

/** A hook's box on the page, off the element that wears its attribute. */
function measureHook(node: Element): Hook {
  const rect = node.getBoundingClientRect();
  return {
    index: Number(node.getAttribute(BENCH_HOOK_ATTRIBUTE)),
    x: rect.left,
    y: rect.top,
    width: rect.width,
    height: rect.height,
  };
}

/** Where a carried tool came from, as the hand remembers it. */
function toolSourceOf(drag: Drag): ToolSource {
  switch (drag.from.kind) {
    case "hook":
      return { kind: "hook" };
    case "arms":
      return { kind: "arms", itemId: drag.material.id };
    case "shelf":
      return {
        kind: "shelf",
        itemId: drag.material.id,
        memberKey: drag.from.member.key,
      };
    case "top":
      // A tool never lies on the top (`liesOnTop`); the type says so.
      throw new Error("A tool can't be carried off the top");
  }
}

/** Whether a thing can lie on the bench top at all: stock does, a tool
 * hangs on the rail instead (it moves between the trays and the hooks). */
function liesOnTop(material: MaterialInstance): boolean {
  return material.type !== "tool";
}

/**
 * Whose shelf a piece let go over the tray goes onto: the table it's
 * nearest, if there's room under it, else any table in the run with
 * room. Null when every shelf is full or there is none.
 */
function shelfMemberFor(
  group: BenchGroup,
  nearest: BenchGroupMember,
): BenchGroupMember | null {
  const hasRoom = (member: BenchGroupMember) =>
    member.machine.shelfSpaceLeft > 0;
  if (hasRoom(nearest)) return nearest;
  return group.members.find(hasRoom) ?? null;
}

/** The member the player opened the view at, or the run's first. */
function openedMember(
  opened: Machine | undefined,
  group: BenchGroup,
): BenchGroupMember {
  return (opened && memberFor(group, opened)) ?? group.members[0];
}

/** The square angle a tilted piece trues to when the hand takes it. */
function nearestRightAngle(angleDeg: number): number {
  return Math.round(angleDeg / 90) * 90;
}

/** The member bookkeeping this piece, or the one it was picked up from. */
function memberOf(
  group: BenchGroup,
  materialId: string,
  fallback: BenchGroupMember,
): BenchGroupMember {
  return (
    group.members.find((member) =>
      [
        ...member.machine.inputMaterials,
        ...member.machine.outputMaterials,
        ...member.machine.storedMaterials,
      ].some((material) => material.id === materialId),
    ) ?? fallback
  );
}
